import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEffect, useState } from 'react';
import api from '../api/axios';
import { applyBrowserBranding } from '../utils/browserBranding';
import { logoUrl } from '../utils/urls';
import OfflineBanner from './OfflineBanner';
import ConnectionStatus from './ConnectionStatus';
import { useDeviceDrafts, useNetworkStatus } from '../offline/useOffline';

export default function AppShell({ title, children, actions, showMobileMenuActions = true }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [companyLogo, setCompanyLogo] = useState('/lazer.png');
  const [companyName, setCompanyName] = useState(process.env.REACT_APP_COMPANY_NAME || 'Lazer');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const offline = useNetworkStatus();
  const deviceDrafts = useDeviceDrafts();

  useEffect(() => {
    api.get('/settings/company').then(({ data }) => {
      setCompanyLogo(logoUrl(data?.company_logo_path));
      setCompanyName(data?.company_name || process.env.REACT_APP_COMPANY_NAME || 'Lazer');
    }).catch(() => {
      setCompanyLogo('/lazer.png');
      setCompanyName(process.env.REACT_APP_COMPANY_NAME || 'Lazer');
    });
  }, []);

  useEffect(() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || navigator.vendor || '' : '';
    setIsIos(/iPad|iPhone|iPod/.test(ua) && !window.MSStream);
  }, []);

  useEffect(() => {
    applyBrowserBranding({
      companyName,
      logoUrl: companyLogo,
      pageLabel: title,
    });
  }, [companyName, companyLogo, title]);

  const doLogout = async () => {
    // تسجيل الخروج دون اتصال يمنع الدخول مرة أخرى حتى يعود الاتصال.
    const warnings = [];
    if (offline) warnings.push('لا يوجد اتصال الآن، ولن تستطيع تسجيل الدخول مرة أخرى حتى يعود الاتصال.');
    if (deviceDrafts.length) warnings.push(`على الجهاز ${deviceDrafts.length} من الفحوصات لم تُرفع بعد. ستبقى محفوظة، وتُرفع بعد الدخول بنفس الحساب.`);
    if (warnings.length && !window.confirm(`${warnings.join('\n\n')}\n\nهل تريد تسجيل الخروج؟`)) return;

    await logout();
    navigate('/login');
  };

  const doGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  return (
    <div
      className="min-h-screen app-shell-root"
      style={{
        backgroundImage: `radial-gradient(circle at top right, rgba(184, 15, 26, 0.14), transparent 30%),radial-gradient(circle at bottom left, rgba(9, 20, 46, 0.12), transparent 26%)`,
        backgroundRepeat: 'no-repeat, no-repeat',
        backgroundSize: 'auto, auto',
        backgroundPosition: 'top right, bottom left',
      }}
    >
      <header
        className="app-shell-header bg-white border-b sticky top-0 z-20"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'auto',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {isIos && (
              <button
                type="button"
                onClick={doGoBack}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-100"
              >
                <span className="text-xl leading-none">←</span>
                رجوع
              </button>
            )}
            <div className="hidden sm:flex h-14 w-24 shrink-0 items-center justify-center rounded-xl border bg-white p-1.5">
              <img src={companyLogo} alt="شعار السيارة" className="block max-h-full max-w-full object-contain" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold text-primary">{title}</h1>
                <ConnectionStatus />
              </div>
              <p className="text-sm text-gray-500">{user?.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowMobileMenu((prev) => !prev)}
              className="sm:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border bg-white text-slate-700 shadow-sm"
              aria-label="قائمة"
            >
              <div className="flex h-full w-full flex-col items-center justify-center gap-1.5">
                <span className="block h-[2px] w-5 rounded-full bg-slate-900" />
                <span className="block h-[2px] w-5 rounded-full bg-slate-900" />
                <span className="block h-[2px] w-5 rounded-full bg-slate-900" />
              </div>
            </button>

            <div className="hidden sm:flex items-center gap-2">
              <Link to="/" className="app-shell-menu-link px-3 py-2 rounded-lg border">الرئيسية</Link>
              <Link to="/sync" className="app-shell-menu-link px-3 py-2 rounded-lg border">
                فحوصات الجهاز{deviceDrafts.length ? ` (${deviceDrafts.length})` : ''}
              </Link>
              {['super_admin', 'admin'].includes(user?.role) && (
                <>
                  <Link to="/master" className="app-shell-menu-link px-3 py-2 rounded-lg border">إعدادات الترميز</Link>
                  <Link to="/settings" className="app-shell-menu-link px-3 py-2 rounded-lg border">الإعدادات</Link>
                </>
              )}
              {actions}
              <button onClick={doLogout} className="app-shell-logout-btn px-3 py-2 rounded-lg bg-red-500 text-white">
                تسجيل خروج
              </button>
            </div>
          </div>
        </div>
      </header>

      <OfflineBanner />

      {showMobileMenu && (
        <div className="sm:hidden px-4 pb-4">
          <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <Link to="/" onClick={() => setShowMobileMenu(false)} className="block rounded-lg border px-4 py-3 text-right text-base font-semibold text-slate-800">الرئيسية</Link>
            <Link to="/sync" onClick={() => setShowMobileMenu(false)} className="mt-2 block rounded-lg border px-4 py-3 text-right text-base font-semibold text-slate-800">
              فحوصات الجهاز{deviceDrafts.length ? ` (${deviceDrafts.length})` : ''}
            </Link>
            {['super_admin', 'admin'].includes(user?.role) && (
              <>
                <Link to="/master" onClick={() => setShowMobileMenu(false)} className="mt-2 block rounded-lg border px-4 py-3 text-right text-base font-semibold text-slate-800">إعدادات الترميز</Link>
                <Link to="/settings" onClick={() => setShowMobileMenu(false)} className="mt-2 block rounded-lg border px-4 py-3 text-right text-base font-semibold text-slate-800">الإعدادات</Link>
              </>
            )}
            {showMobileMenuActions && actions && <div className="mt-2">{actions}</div>}
            <button
              onClick={() => {
                setShowMobileMenu(false);
                doLogout();
              }}
              className="mt-4 w-full rounded-lg bg-red-500 px-4 py-3 text-base font-semibold text-white"
            >
              تسجيل خروج
            </button>
          </div>
        </div>
      )}

      <main
        className="app-shell-main app-shell-logo-theme w-full sm:max-w-7xl mx-auto p-4"
        style={{
          position: 'relative',
          isolation: 'isolate',
          minWidth: 0,
        }}
      >
        <div
          style={{
            content: "''",
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            backgroundImage: `url(${companyLogo}), url(${companyLogo})`,
            backgroundRepeat: 'no-repeat, no-repeat',
            backgroundSize: '220px auto, 160px auto',
            backgroundPosition: 'right 32px top 24px, left 32px bottom 24px',
            opacity: 0.045,
          }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
      </main>
    </div>
  );
}
